import React, { useState, useRef, useCallback } from 'react';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';

const ResizeHandle = styled(Box)(({ theme }) => ({
  width: 8,
  backgroundColor: theme.palette.divider,
  cursor: 'col-resize',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
  },
  '&:active': {
    backgroundColor: theme.palette.primary.dark,
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    width: 2,
    height: 20,
    backgroundColor: 'currentColor',
    borderRadius: 1,
  },
}));

export default function ResizableSplitView({
  leftPanel,
  rightPanel,
  initialLeftWidth = 50,
  minLeftWidth = 30,
  maxLeftWidth = 70,
}) {
  const [leftWidth, setLeftWidth] = useState(initialLeftWidth);
  const [isResizing, setIsResizing] = useState(false);
  const containerRef = useRef(null);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);

  const handleMouseDown = useCallback((e) => {
    setIsResizing(true);
    startXRef.current = e.clientX;
    startWidthRef.current = leftWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [leftWidth]);

  const handleMouseMove = useCallback((e) => {
    if (!isResizing || !containerRef.current) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const containerWidth = containerRect.width;
    const deltaX = e.clientX - startXRef.current;
    const deltaPercent = (deltaX / containerWidth) * 100;

    const newLeftWidth = Math.max(
      minLeftWidth,
      Math.min(maxLeftWidth, startWidthRef.current + deltaPercent),
    );

    setLeftWidth(newLeftWidth);
  }, [isResizing, minLeftWidth, maxLeftWidth]);

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  React.useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
    return undefined;
  }, [isResizing, handleMouseMove, handleMouseUp]);

  return (
    <Box
      ref={containerRef}
      sx={{
        display: 'flex',
        height: '100%',
        width: '100%',
        overflow: 'hidden',
      }}
    >
      {/* Left Panel */}
      <Box
        sx={{
          width: `${leftWidth}%`,
          height: '100%',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {leftPanel}
      </Box>

      {/* Resize Handle */}
      <ResizeHandle
        onMouseDown={handleMouseDown}
        sx={{
          opacity: isResizing ? 1 : 0.7,
          '&:hover': {
            opacity: 1,
          },
        }}
      />

      {/* Right Panel */}
      <Box
        sx={{
          width: `${100 - leftWidth}%`,
          height: '100%',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {rightPanel}
      </Box>
    </Box>
  );
}
